import { Capacitor } from '@capacitor/core';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';

/**
 * RevenueCat wraps Google Play Billing so Android can sell the same
 * subscription Stripe sells on web, without us re-implementing purchase
 * verification, receipt validation, or renewal/grace-period handling.
 *
 * Gated on both platform and key presence — same pattern as initSentry/
 * initAnalytics. Until VITE_REVENUECAT_ANDROID_API_KEY is set (it can't be,
 * until Play Console has a subscription product and RevenueCat is linked to
 * it — see PLAY_BILLING_PLAN.md), every function here is a safe no-op and
 * utils/purchaseAvailability keeps reporting no purchase path on Android,
 * exactly like today.
 */

const ANDROID_API_KEY = process.env.VITE_REVENUECAT_ANDROID_API_KEY || '';

function isSupportedPlatform(): boolean {
  // Every exported function here is documented as a safe no-op, so this
  // must never throw regardless of what Capacitor.getPlatform() does.
  try {
    return Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

export function isRevenueCatConfigured(): boolean {
  return isSupportedPlatform() && !!ANDROID_API_KEY;
}

let configurePromise: Promise<void> | null = null;

/**
 * Configures the SDK once, before any other call. Idempotent — safe to call
 * from multiple mount points; later callers just await the same promise.
 */
export function initRevenueCat(): Promise<void> {
  if (!isRevenueCatConfigured()) return Promise.resolve();
  if (!configurePromise) {
    configurePromise = import('@revenuecat/purchases-capacitor')
      .then(({ Purchases, LOG_LEVEL }) => {
        // Errors surface as rejections from later calls (getOfferings,
        // purchasePackage) rather than here, so callers see them at the
        // point that actually matters to the user.
        Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
        return Purchases.configure({ apiKey: ANDROID_API_KEY });
      })
      .catch((err: unknown) => {
        console.error('[RevenueCat] configure failed:', err);
      });
  }
  return configurePromise;
}

/**
 * The RevenueCat identity is an opaque random UUID, never the Firebase uid.
 * Purchases.logIn() is an unauthenticated call made with a public API key,
 * so a uid passed there could be asserted by anyone who knows it — see
 * functions/src/revenuecatIdentity.ts for the full reasoning. The UUID means
 * nothing until linkRevenueCatUser binds it to the caller's real, verified
 * Firebase account.
 *
 * Keyed by Firebase uid rather than one id per device: on a shared phone,
 * user B signing in after user A must not inherit A's purchaser identity and
 * with it A's future renewals.
 */
const PURCHASER_ID_PREFIX = 'rc_purchaser_id_';

// Used only where localStorage is unavailable (storage blocked). Stable for
// the life of the app process, so a purchase started in this session stays
// resolvable; a fresh id next launch is simply linked again at sign-in.
const memoryPurchaserIds = new Map<string, string>();

function getOrCreatePurchaserId(firebaseUid: string): string {
  const key = `${PURCHASER_ID_PREFIX}${firebaseUid}`;
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
  } catch {
    // Storage unavailable — fall through to the in-memory map.
  }

  const cached = memoryPurchaserIds.get(firebaseUid);
  if (cached) return cached;

  const fresh = crypto.randomUUID();
  memoryPurchaserIds.set(firebaseUid, fresh);
  try {
    localStorage.setItem(key, fresh);
  } catch {
    // Memory-only for this session.
  }
  return fresh;
}

let identity: { uid: string; purchaserId: string; ready: boolean } | null = null;

async function establishIdentity(): Promise<void> {
  if (!identity) throw new Error('No signed-in user for RevenueCat identity');
  const { purchaserId } = identity;

  await initRevenueCat();

  // Link BEFORE logIn, not after. A purchase made under an id the server
  // can't resolve produces a webhook that retries for ~2.6 hours and is then
  // dropped — the user would have paid and received nothing. Linking first
  // means the id is always resolvable by the time anything can be bought
  // under it.
  const res = await authFetch(FUNCTION_URLS.linkRevenueCatUser, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ purchaserId }),
  });
  if (!res.ok) throw new Error(`linkRevenueCatUser ${res.status}`);

  const { Purchases } = await import('@revenuecat/purchases-capacitor');
  await Purchases.logIn({ appUserID: purchaserId });

  identity.ready = true;
}

/**
 * Establishes the RevenueCat identity for the signed-in user. Best-effort:
 * signing in must not fail because RevenueCat or our own linking endpoint is
 * unreachable. Anything that failed here is retried by
 * ensureRevenueCatIdentity() before a purchase can proceed.
 */
export async function loginRevenueCat(uid: string): Promise<void> {
  if (!isRevenueCatConfigured()) return;
  identity = { uid, purchaserId: getOrCreatePurchaserId(uid), ready: false };
  try {
    await establishIdentity();
  } catch (err: unknown) {
    console.error('[RevenueCat] identity setup failed:', err);
  }
}

/**
 * Strict counterpart to loginRevenueCat, for the purchase paths: throws
 * rather than letting a charge happen under an identity the webhook would
 * not be able to attribute to anyone.
 */
export async function ensureRevenueCatIdentity(): Promise<void> {
  if (!isRevenueCatConfigured()) return;
  if (identity?.ready) return;
  await establishIdentity();
}

/**
 * Best-effort only. Purchases already belong to the store account (Google
 * Play), not to our session, so a failure here can't strand or misattribute
 * a subscription — it only delays RevenueCat resetting to an anonymous id.
 */
export async function logoutRevenueCat(): Promise<void> {
  identity = null;
  if (!isRevenueCatConfigured()) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logOut();
  } catch (err: unknown) {
    console.error('[RevenueCat] logOut failed:', err);
  }
}
